import { Component, Input, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { PaykuService } from 'src/app/services/payku.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { BancoEnt } from 'src/app/models/Payku/Banco';
import { CreateClientForm, BankAccount } from 'src/app/models/Payku/CreateClientForm';
import { CuentaBancariaClienteEnt } from 'src/app/models/Payku/CuentaBancariaCliente';
import { TokenStorageService } from 'src/app/services/token-storage.service';
import { TourService } from 'src/app/services/tour-tutorial.service';
import { ConfiguracionesTour } from 'src/app/shared/models/tour-tutorial-configurations';

@Component({
  selector: 'app-payku-create-form',
  templateUrl: './payku-create-form.component.html',
  styleUrls: ['./payku-create-form.component.scss']
})
export class PaykuCreateFormComponent implements OnInit {
  @Input() bancos: BancoEnt[] = [];
  @Input() datosBancarios: CuentaBancariaClienteEnt[] = [];
  @Input() onCreateClient!: (clientData: CreateClientForm) => void;
  @Input() iniciarTour: boolean = false;
  
  form!: FormGroup;
  emisorId: string = ''; 
  usuarioId: string = '';
  usarDatosAnteriores: boolean = false; 
  
  accountTypes = [
    { id: 1, label: 'Cuenta Corriente' },
    { id: 2, label: 'Cuenta Vista/RUT' },
    { id: 3, label: 'Cuenta de Ahorro' },
  ];

  constructor(
    private fb: FormBuilder,
    public activeModal: NgbActiveModal,
    private tokenService: TokenStorageService,
    private tourService: TourService,
    private paykuService: PaykuService
  ) { }

  ngOnInit(): void {
    this.form = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      name: ['', [Validators.required, Validators.minLength(2), this.nameNoNumberValidator]],
      phone: ['', [Validators.required, this.phoneValidator]],
      rut: ['', [Validators.required, this.rutValidator]],
      sbif: ['', Validators.required],
      type: ['', Validators.required],
      num: ['', [Validators.required, Validators.pattern(/^\d{6,40}$/)]],
      cuentaBancariaAnterior: [''], 
      terms: [false, Validators.requiredTrue],
      termsAlianza: [false, Validators.requiredTrue],
    });

    this.form.get('rut')?.valueChanges.subscribe(value => {
      if (value && typeof value === 'string') {
        const formatted = this.formatRut(value);
        if (formatted !== value) {
          this.form.get('rut')?.setValue(formatted, { emitEvent: false });
        }
      }
    });

    this.form.get('phone')?.valueChanges.subscribe(value => {
      if (value && typeof value === 'string') {
        const formatted = this.formatPhone(value);
        if (formatted !== value) {
          this.form.get('phone')?.setValue(formatted, { emitEvent: false });
        }
      }
    });
    // Evitar que se ingresen letras en el número de cuenta: limpiar todo lo que no sea dígito
    this.form.get('num')?.valueChanges.subscribe(value => {
      if (value && typeof value === 'string') {
        const cleaned = value.replace(/\D/g, '');
        if (cleaned !== value) {
          this.form.get('num')?.setValue(cleaned, { emitEvent: false });
        }
      }
    });

    // Evitar que se ingresen números en el nombre: eliminar dígitos en tiempo real
    this.form.get('name')?.valueChanges.subscribe(value => {
      if (value && typeof value === 'string') {
        const cleaned = value.replace(/\d/g, '');
        if (cleaned !== value) {
          this.form.get('name')?.setValue(cleaned, { emitEvent: false });
        }
      }
    });
    if (!this.emisorId) {
      this.emisorId = this.tokenService.getCompany();
    }

    if (!this.usuarioId) {
      this.usuarioId = this.tokenService.getUser().id;
    }

    this.actualizarValidaciones();

    if (this.iniciarTour) {
      setTimeout(() => {
        this.iniciarTourFormulario();
      }, 1000);
    }
  }

  get f() { return this.form.controls; }
  toggleUsarDatosAnteriores(): void {
    this.actualizarValidaciones();
  }


  private actualizarValidaciones(): void {
    if (this.usarDatosAnteriores) {
      this.form.get('rut')?.clearValidators();
      this.form.get('sbif')?.clearValidators();
      this.form.get('type')?.clearValidators();
      this.form.get('num')?.clearValidators();
      this.form.get('cuentaBancariaAnterior')?.setValidators([Validators.required]);
    } else {
      this.form.get('rut')?.setValidators([Validators.required, this.rutValidator]);
      this.form.get('sbif')?.setValidators([Validators.required]);
      this.form.get('type')?.setValidators([Validators.required]);
      this.form.get('num')?.setValidators([Validators.required, Validators.pattern(/^\d{6,40}$/)]);
      this.form.get('cuentaBancariaAnterior')?.clearValidators();
    }
    
    this.form.get('rut')?.updateValueAndValidity();
    this.form.get('sbif')?.updateValueAndValidity();
    this.form.get('type')?.updateValueAndValidity();
    this.form.get('num')?.updateValueAndValidity();
    this.form.get('cuentaBancariaAnterior')?.updateValueAndValidity();
  }

  getCuentaDisplayText(cuenta: CuentaBancariaClienteEnt): string {
    const banco = this.bancos.find(b => b.sbif === cuenta.sbif);
    const bancoNombre = banco ? banco.nombre : 'Banco desconocido';
    const tipoNombre = this.accountTypes.find(t => t.id === cuenta.tipoCuentaId)?.label || 'Tipo desconocido';
    
    return `${bancoNombre} - ${tipoNombre} - RUT: ${cuenta.rut} - **** ${cuenta.ultimos4Numero}`;
  }

  rutValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;
    
    const rutClean = control.value.toString().replace(/[^0-9kK]/g, '');
    if (rutClean.length < 2) return { rutInvalido: true };
    
    const dv = rutClean.slice(-1).toUpperCase();
    const rutNumber = rutClean.slice(0, -1);
    
    if (!/^\d+$/.test(rutNumber)) return { rutInvalido: true };
    
    let suma = 0;
    let multiplicador = 2;
    
    for (let i = rutNumber.length - 1; i >= 0; i--) {
      suma += parseInt(rutNumber.charAt(i)) * multiplicador;
      multiplicador = multiplicador === 7 ? 2 : multiplicador + 1;
    }
    
    const dvCalculado = 11 - (suma % 11);
    const dvEsperado = dvCalculado === 11 ? '0' : dvCalculado === 10 ? 'K' : dvCalculado.toString();
    
    return dv === dvEsperado ? null : { rutInvalido: true };
  }

  nameNoNumberValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;
    const hasNumber = /\d/.test(control.value.toString());
    return hasNumber ? { containsNumber: true } : null;
  }

  phoneValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;
    
    const phoneClean = control.value.toString().replace(/\D/g, '');
    const length = phoneClean.length;
    
    if (length < 9 || length > 12) {
      return { pattern: true };
    }
    
    return null;
  }

  formatRut(rut: string): string {
    const rutClean = rut.replace(/[^0-9kK]/g, '');
    if (rutClean.length <= 1) return rutClean;
    
    const dv = rutClean.slice(-1);
    const number = rutClean.slice(0, -1);
    
    if (number.length === 0) return rutClean;
    
    const numberFormatted = number.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `${numberFormatted}-${dv}`;
  }

  formatPhone(phone: string): string {
    const phoneClean = phone.replace(/\D/g, '');
    if (phoneClean.length <= 1) return phoneClean;
    
    if (phoneClean.length <= 5) {
      return phoneClean.replace(/(\d{1})(\d{0,4})/, '$1 $2').trim();
    } else {
      return phoneClean.replace(/(\d{1})(\d{4})(\d{0,4})/, '$1 $2 $3').trim();
    }
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const createClientForm: CreateClientForm = {
      email: this.form.value.email,
      name: this.form.value.name,
      phone: this.form.value.phone.replace(/\D/g, ''),
      emisorId: this.emisorId,
      usuarioId: this.usuarioId
    };

    if (this.usarDatosAnteriores) {
      createClientForm.cuentaBancariaId = this.form.value.cuentaBancariaAnterior;
    } else {
      createClientForm.bank = {
        sbif: this.form.value.sbif,
        type: this.form.value.type,
        num: this.form.value.num,
        rut: this.form.value.rut.replace(/[^0-9kK]/g, '')
      };
    }
    
    this.onCreateClient(createClientForm);
  }

  downloadTermsPdf(): void {
    this.paykuService.getTermsConditionsPdf().subscribe(
      resp => {
        const blob = resp.body as Blob;
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = 'terms.pdf';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(downloadUrl);
      },
      err => {
        console.error('Error descargando términos desde PaykuService:', err);
      }
    );
  }

  private iniciarTourFormulario(): void {
    const configFormulario = {    ...ConfiguracionesTour.PAYKU_FORMULARIO};

    this.tourService.iniciarTour(configFormulario, {
      enCambioPaso: (indice: number) => {
        this.manejarCambioPasoFormulario(indice);
      },
      alFinalizar: () => {
        console.log('Tour del formulario Payku finalizado');
      }
    });
  }

  private manejarCambioPasoFormulario(indice: number): void {
    if (indice === 3) { 
      const toggleElement = document.querySelector('#usarDatosAnteriores') as HTMLInputElement;
      if (toggleElement && toggleElement.checked) {
        toggleElement.click();
      }
    }
  }
}
